/**
 * The install-prompt banner, shared across every screen that wants to offer it.
 *
 * ── Why this is its own file ──────────────────────────────────────────────────────────────────
 *
 * `LocalRun.tsx` built this once, for itself, with copy specific to the CSV-upload flow
 * ("your data is already on this device, so there is nothing to fetch"). That is true on
 * `#/local` and **false** everywhere else this app runs — a connected Salesforce/HubSpot org's
 * data is not "already on this device", it is synced from the customer's own CRM. Extracting the
 * polling mechanics here, with the pitch passed in as `children`, is what lets `Connect.tsx` (the
 * OAuth connect screen) and `Sources.tsx` offer the SAME install capability with copy that is
 * actually true for what they show — rather than either duplicating the polling logic per screen
 * or reusing local-mode-specific copy somewhere it does not apply.
 *
 * ── Why this exists at all ────────────────────────────────────────────────────────────────────
 *
 * The mechanics (`captureInstallPrompt`/`canInstall`/`promptInstall`/`isInstalled` in `pwa.ts`)
 * were built and worked, but the only place that ever rendered them was the CSV-upload screen —
 * reachable only from local mode, and never from the moment a client is actually deciding to
 * connect a real system with their own credentials. `StartTrial.tsx` went as far as *promising* an
 * install ("install Dedupr on this machine") in prose with no button anywhere on the page that
 * could do it. This component is what lets every one of those places make good on the promise
 * with one shared implementation.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Callout } from '@dedupr/ui';
import { canInstall, isInstalled, promptInstall } from './pwa.js';

export interface InstallOfferProps {
  /** The screen-specific pitch. Rendered before the Install button, in the same sentence flow. */
  children: ReactNode;
  /** Sentence-case, defaults to the phrase every caller has used so far. */
  title?: string;
}

/**
 * Offer to install, when the browser says it can be installed.
 *
 * ⚠️ **Rendered only when the prompt genuinely exists.** `beforeinstallprompt` fires once per page
 * load and is single-use, so a permanently-visible Install button is one that does nothing for most
 * visitors — already installed, unsupported browser, or prompt already dismissed. A button that does
 * nothing is worse than no button.
 */
export function InstallOffer({ children, title = 'Install this as an app' }: InstallOfferProps) {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    /* Polled briefly rather than subscribed: `captureInstallPrompt` owns the listener (it has to run
       before React mounts, or the event is missed), so this asks it whether the prompt arrived.
       Bounded to ~5s — Chrome fires the event during load or not at all, so waiting longer would only
       keep a timer alive for nothing. */
    if (isInstalled()) return;
    let tries = 0;
    const timer = setInterval(() => {
      if (canInstall()) {
        setAvailable(true);
        clearInterval(timer);
      } else if (++tries > 10) {
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, []);

  if (!available) return null;

  return (
    <Callout tone="info" title={title}>
      {children}{' '}
      <Button
        size="sm"
        variant="secondary"
        onClick={async () => {
          const outcome = await promptInstall();
          // Either way the prompt is spent, so the offer goes rather than lingering as a dead button.
          if (outcome !== null) setAvailable(false);
        }}
      >
        Install
      </Button>
    </Callout>
  );
}
